import { NavLink, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import styles from "./YearPlanTableItem.module.css";

import { selectAuthUserId, selectLoginToken } from "../../features/auth/AuthSlice";
import { useSaveYearPlan } from "../../features/yearPlan/hooks/useSaveYearPlan";
import { useSaveSubjectPlanInYearPlan } from "../../features/yearPlan/hooks/useSaveSubjectPlanInYearPlan";
import { useDeleteYearPlan } from "../../features/yearPlan/hooks/useDeleteYearPlan";

import {
  IconDuplicate,
  IconEye,
  IconShare,
  IconTrash,
} from "../../common/Icons/Icons";

const YearPlanTableItem = ({
  yearPlan,
  showShareModal,
  freezeClassName,
  displaySharedOnly,
}) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation("subjectPlan");
  const currentUserId = useSelector(selectAuthUserId);
  const jwt = useSelector(selectLoginToken);

  // Simple JWT decoder function
  const decodeJWT = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
      return JSON.parse(jsonPayload);
    } catch (err) {
      return null;
    }
  };

  // Get user's group roles from JWT
  const getUserGroupRoles = () => {
    try {
      if (!jwt) return [];
      const decoded = decodeJWT(jwt);
      return decoded?.groupRoles || [];
    } catch (err) {
      return [];
    }
  };

  // Check if user has write/admin access through groups
  const hasGroupWriteAccess = () => {
    const userGroupRoles = getUserGroupRoles();
    const groupAccessRecords = yearPlan.access?.filter(a => a.groupId && (a.access === 'W' || a.access === 'A')) || [];

    for (const groupAccess of groupAccessRecords) {
      const userRole = userGroupRoles.find(gr => gr.groupId === groupAccess.groupId);
      // User has write access if they're admin or write in a group with 'W' or 'A' access
      if (userRole && ['admin', 'write'].includes(userRole.roleName)) {
        return true;
      }
    }
    return false;
  };

  // Check if user has direct Admin access to the year plan
  const hasDirectAdminAccess = yearPlan.access?.some(
    access => access.userId === currentUserId && access.access === 'A'
  );

  // Check if user has direct Write access
  const hasDirectWriteAccess = yearPlan.access?.some(
    access => access.userId === currentUserId && (access.access === 'W' || access.access === 'A')
  );

  // User can share if they own the plan OR have Admin/Write access (direct or through groups)
  const canShare = yearPlan.userId === currentUserId ||
                   hasDirectAdminAccess ||
                   hasDirectWriteAccess ||
                   hasGroupWriteAccess();

  // Determine how the user has access to this plan
  const getAccessVia = () => {
    if (!yearPlan.access || yearPlan.access.length === 0) return null;

    // Check for direct user access
    const directAccess = yearPlan.access.find(
      acc => acc.userId === currentUserId && !acc.groupId
    );
    if (directAccess) return t('common:direct');

    // Check for group access
    const groupAccess = yearPlan.access.find(acc => acc.groupId && acc.group);
    if (groupAccess) return groupAccess.group.name;

    return null;
  };

  const { mutate: deleteYearPlan } = useDeleteYearPlan();
  const { mutate: saveYearPlan } = useSaveYearPlan();
  const { mutate: saveSubjectPlanInYearPlan } = useSaveSubjectPlanInYearPlan();

  function handleDeleteItem() {
    const decision = window.confirm(
      t("common:confirmations.removeSubjectPlan")
    );

    if (decision) deleteYearPlan(yearPlan.id);
  }

  function handleDisplayItem() {
    history.push(`/arsplan/${yearPlan.id}`);
    window.scrollTo({
      top: 0,
    });
  }

  function handleShareButtonClick() {
    showShareModal(yearPlan.id);
  }

  function handleDuplicatePlan() {
    const newYearPlan = {
      year: yearPlan.year || null,
      subject: yearPlan.subject || "",
      grade: yearPlan.grade || null,
      title: `${yearPlan.title || t("common:unknown")} - kopi`,
    };

    saveYearPlan(newYearPlan, {
      onSuccess: (response) => {
        const newYearPlanId = response.data?.id;

        if (yearPlan.plans && yearPlan.plans.length > 0) {
          for (const plan of yearPlan.plans) {
            const updatedSubjectPlan = {
              yearPlanId: newYearPlanId,
              planId: plan.planId,
              fromDate: plan.fromDate,
              toDate: plan.toDate,
            };

            saveSubjectPlanInYearPlan(updatedSubjectPlan, {
              onSuccess: () => {
                queryClient.invalidateQueries("yearPlans");
              },
            });
          }
        }

        history.push(`/arsplan/${newYearPlanId}`);
      },
    });
  }

  return (
    <tr className={styles.row}>
      <td className={freezeClassName}>
        <NavLink to={`/arsplan/${yearPlan.id}`}>
          {yearPlan.title || "-"}
        </NavLink>
      </td>

      <td>{yearPlan.year ? `${yearPlan.year} - ${yearPlan.year + 1}` : ""}</td>

      <td className={styles.freezeSubject}>
        {yearPlan.subject.split(";").map((item, key) => (
          <span key={key}>
            {item}
            <br />
          </span>
        )) || " "}
      </td>

      <td>{yearPlan.grade ? `${yearPlan.grade}. trinn` : ""}</td>

      <td>{new Date(yearPlan.updatedAt).toLocaleDateString("en-GB")}</td>

      {displaySharedOnly && <td>{getAccessVia()}</td>}

      <td className={styles.icons}>
        <button title="Åpne" onClick={handleDisplayItem}>
          <IconEye />
        </button>

        {canShare && (
          <button title="Del" onClick={handleShareButtonClick}>
            <IconShare />
          </button>
        )}

        <button title="Dupliser" onClick={handleDuplicatePlan}>
          <IconDuplicate />
        </button>

        {displaySharedOnly ? null : (
          <button title="Slett" onClick={handleDeleteItem}>
            <IconTrash />
          </button>
        )}
      </td>
    </tr>
  );
};

export default YearPlanTableItem;
